/**
 * FormPage.tsx
 * Main form component for creating and editing pitch submissions.
 * Handles form state management and submission logic using Formik.
 */

import React, { useState, useRef } from "react";
import Button from "../components/ui/Button";
import { swalColors } from "../utils/swal";
import OnInvalidSubmit from "../components/common/OnInvalidSubmit";
import { useNavigate, useLocation } from "react-router-dom";
import { Formik, Form, FormikProps } from "formik";
import ReleaseInfo from "./components/ReleaseInfo/ReleaseInfo";
import PlatformSelection from "./components/PlatformSelector/PlatformSelection";
import GeneralReleaseInfo from "./components/GeneralReleaseInfo/GeneralReleaseInfo";
import DspSpecificInfo, { hasDspSpecificFields } from "./components/DSPSpecificInfo/DspSpecificInfo";
import { SelectedDSPs, FormPageProps } from "../PitchFormPage/types/form";
import {
  createPitchValidationSchema,
  PitchFormValues,
} from "./components/Schema/pitchValidationSchema";

import { pitchesApi } from "../repositories/pitches";
import { EnvelopeIcon } from "@heroicons/react/24/outline";
import Breadcrumbs, { HOME_CRUMB } from "../components/ui/Breadcrumbs";
import Swal from "sweetalert2";
import { dspApi } from "../repositories/dsp";
import { useAppSelector } from '../hooks/redux';
import { useDspArtists } from "../hooks/useDSpArtists";
import { validateSelectedDSPs } from "./utils/dspValidation";
import { handleUpcConflict } from "./utils/upcConflict";
import AddNewArtistModal from "../components/modal/AddNewArtistModal";
import { soundchartsEndpoints } from "../repositories/soundcharts";
import { oauthApi } from "../repositories/oauth";
import EmailPitchModal from "../components/modal/EmailPitchModal";
import { EmailContact, PersonalizedMessage } from "../types/email";
import { downloadAppleCsv } from "../utils/pitchCsvDownload";
import ProvisionalFieldsControl from "./components/ProvisionalFields/ProvisionalFieldsControl";
import { selectCompanyType } from "../store/slices/userSlice";
import { classifyCompanyType } from "../utils/companyTypeClassification";


const FormPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAppSelector((state) => state.auth);
  const companyType = useAppSelector(selectCompanyType);
  const companyTypeCategory = classifyCompanyType(companyType);
  const {
    submissionData = {},
    releaseData = {},
    isEditing = false,
  } = (location.state as FormPageProps) || {};

  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
  const [isDrafting, setIsDrafting] = React.useState<boolean>(false);
  // Remove showErrorSummary state since we'll use toast instead
  const [isAddArtistModalOpen, setIsAddArtistModalOpen] = useState(false);
  // Use the DSP artists hook to get connected accounts
  const { dspList, fetchDSPs } = useDspArtists();
  // arits id save in pitch
  const [artistId, setArtistId] = useState<string>("");
  // Email pitch modal states
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [emailModalData, setEmailModalData] = useState<{
    contactIds: number[];
    templateId: string;
    subject: string;
    templateContent?: string;
    selectedContacts?: EmailContact[];
    personalizedMessages?: PersonalizedMessage[];
  }>({ contactIds: [], templateId: '', subject: '' });
  // Track if email data has been configured (for showing Edit Email button)
  const [hasEmailData, setHasEmailData] = useState(false);
  // Memoized so the modal's open/reset effect (which lists initialEmailData as a
  // dependency) doesn't re-fire on every render and wipe in-progress selections.
  const initialEmailData = React.useMemo(
    () =>
      hasEmailData
        ? {
          contactIds: emailModalData.contactIds,
          templateId: emailModalData.templateId,
          subject: emailModalData.subject,
          personalizedMessages: emailModalData.personalizedMessages,
        }
        : undefined,
    [
      hasEmailData,
      emailModalData.contactIds,
      emailModalData.templateId,
      emailModalData.subject,
      emailModalData.personalizedMessages,
    ]
  );
  const [pendingFormValues, setPendingFormValues] = useState<PitchFormValues | null>(null);



  // Formik ref to access form values
  const formikRef = useRef<FormikProps<PitchFormValues>>(null);

  // DSP mapping - maps selectedDSPs keys to display names
  const dspMapping: Record<keyof SelectedDSPs, string> = {
    spotify: "Spotify",
    amazon: "Amazon Music",
    deezer: "Deezer",
    audiomack: "Audiomack",
    apple: "Apple Music",
    TIDAL: "TIDAL",
    youtube: "YouTube",
    soundcloud: "SoundCloud",
    email: "Email",
    siriusxm: "SiriusXM",
    vevo: "Vevo"
  };

  // Handler for DSP deselection warning
  const handleDspDeselect = async (dspKey: keyof SelectedDSPs): Promise<boolean> => {
    const dspName = dspMapping[dspKey];
    const formValues = formikRef.current?.values;

    // Check if there are marketing notes for this DSP
    const dspNotes = formValues?.dspMarketingNotes || [];
    const hasNote = dspNotes.some((note: any) => note.dspName === dspName);

    // Check if there are platform-specific UPCs for this DSP
    const dspUpcs = formValues?.platformSpecificUpcs || [];
    const matchingUpcs = dspUpcs.filter((entry: any) => entry.dspName === dspName);
    const hasUpc = matchingUpcs.length > 0;

    if (hasNote || hasUpc) {
      const items: string[] = [];
      if (hasNote) items.push("marketing notes");
      if (hasUpc) items.push(`${matchingUpcs.length} platform-specific UPC(s)`);

      // Show confirmation modal
      const result = await Swal.fire({
        title: 'Warning',
        html: `You have ${items.join(" and ")} for <strong>${dspName}</strong>. If you deselect this platform, they will be deleted. Continue?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Remove and Deselect',
        cancelButtonText: 'Keep Platform Selected',
        confirmButtonColor: swalColors.danger,
        reverseButtons: true
      });

      if (result.isConfirmed) {
        // Delete the notes
        if (hasNote) {
          const updatedNotes = dspNotes.filter((note: any) => note.dspName !== dspName);
          formikRef.current?.setFieldValue('dspMarketingNotes', updatedNotes);
        }
        // Delete the UPCs
        if (hasUpc) {
          const updatedUpcs = dspUpcs.filter((entry: any) => entry.dspName !== dspName);
          formikRef.current?.setFieldValue('platformSpecificUpcs', updatedUpcs);
        }
        return true; // Proceed with deselection
      } else {
        return false; // Cancel deselection
      }
    }

    return true; // No notes or UPCs, proceed with deselection
  };

  // Fetch connected DSP accounts on mount
  React.useEffect(() => {
    fetchDSPs();
  }, []);

  // Initialize selected platforms
  const [selectedDSPs, setSelectedDSPs] = React.useState<SelectedDSPs>({
    spotify: Boolean(submissionData?.selectedPlatforms?.includes("spotify")),
    amazon: Boolean(submissionData?.selectedPlatforms?.includes("amazon")),
    deezer: Boolean(submissionData?.selectedPlatforms?.includes("deezer")),
    audiomack: Boolean(
      submissionData?.selectedPlatforms?.includes("audiomack")
    ),
    soundcloud: Boolean(
      submissionData?.selectedPlatforms?.includes("soundcloud")
    ),
    apple: Boolean(submissionData?.selectedPlatforms?.includes("apple")),
    TIDAL: Boolean(submissionData?.selectedPlatforms?.includes("TIDAL")),
    youtube: Boolean(submissionData?.selectedPlatforms?.includes("youtube")),
    email: Boolean(submissionData?.selectedPlatforms?.includes("email")),
    siriusxm: Boolean(submissionData?.selectedPlatforms?.includes("siriusxm")),
    vevo: Boolean(submissionData?.selectedPlatforms?.includes("vevo")),
  });

  // Initial form values
  const initialValues: PitchFormValues = {
    // Basic Artist and Release Information
    artistName: releaseData?.artist || "",


    releaseTitle: releaseData?.release || "",
    releaseDate: releaseData?.releaseDate || "",
    configuration: submissionData?.configuration
      ? (typeof submissionData.configuration === 'string'
          ? (submissionData.configuration as string).split(', ').map((s: string) => s.trim()).filter((s: string) => s.length > 0)
          : submissionData.configuration)
      : [],

    // Identifiers
    releaseUpc: submissionData?.releaseUpc || "",
    focusTrackName: submissionData?.focusTrackName || "",
    trackIsrc: submissionData?.trackIsrc || "",

    // Artist Details
    featuredArtists: submissionData?.featuredArtists || "",
    featuredArtistUri: submissionData?.featuredArtistUri || "",
    artistHometown: submissionData?.artistHometown || "",
    artistCurrentTown: submissionData?.artistCurrentTown || "",

    // Release Descriptions
    altTitles: submissionData?.altTitles || "",
    shortDescription: submissionData?.shortDescription || "",
    expandedDescription: submissionData?.expandedDescription || "",
    longDescription: submissionData?.longDescription || "",
    lyrics: submissionData?.lyrics || "",
    songDescription: submissionData?.songDescription || [],

    // Musical Characteristics
    mainGenre: submissionData?.mainGenre || [],
    songMood: submissionData?.songMood || [],
    language: submissionData?.language || [],

    // Links and Media
    spotifyUri: submissionData?.spotifyUri || "",
    linkToListen: submissionData?.linkToListen || "",
    promoPhotos: submissionData?.promoPhotos || "",
    promoPhotosCredit: submissionData?.promoPhotosCredit || "",
    socialsLinks: submissionData?.socialsLinks || "",

    // Platform-Specific Details
    similarArtists: submissionData?.similarArtists || "",
    asin: submissionData?.asin || "",
    TIDALpage: submissionData?.TIDALpage || "",
    soundCloudProfileUrl: submissionData?.soundCloudProfileUrl || "",
    soundCloudContact: submissionData?.soundCloudContact || "",
    distributor: submissionData?.distributor || "",
    appleId: submissionData?.appleId || "",
    appleTrackId: submissionData?.appleTrackId || "",
    appleAlbumId: submissionData?.appleAlbumId || "",
    appleSubgenre: submissionData?.appleSubgenre || [],
    appleReleaseStage: submissionData?.appleReleaseStage || "",
    applePitchFrom: submissionData?.applePitchFrom || "",
    applePitchTo: submissionData?.applePitchTo || "",
    appleHasTimedRelease: submissionData?.appleHasTimedRelease || "",
    appleTimedReleaseDate: submissionData?.appleTimedReleaseDate || "",
    appleHasMotionArtwork: submissionData?.appleHasMotionArtwork || "",
    appleHasSpatialAudio: submissionData?.appleHasSpatialAudio || "",
    appleLabel: submissionData?.appleLabel || "",
    applePromotionDate: submissionData?.applePromotionDate || "",
    appleCountry: submissionData?.appleCountry || "",

    // Platform-Specific Flags and Priorities
    pinToThisIsPlaylist: submissionData?.pinToThisIsPlaylist || false,
    pitchToAudiomackTrending: submissionData?.pitchToAudiomackTrending || false,
    isPremierProgram: submissionData?.isPremierProgram || false,

    //     // Subgenres
    spotifySubgenre: submissionData?.spotifySubgenre || [],
    amazonSubgenre: submissionData?.amazonSubgenre || [],
    deezerSubgenre: submissionData?.deezerSubgenre || [],
    audiomackSubgenre: submissionData?.audiomackSubgenre || [],
    TIDALSubgenre: submissionData?.TIDALSubgenre || [],
    soundCloudSubgenre: submissionData?.soundCloudSubgenre || [],

    // Spotify specific
    musicCulture: submissionData?.musicCulture || [],
    instruments: submissionData?.instruments || [],
    songStyle: submissionData?.songStyle || [],

    // Amazon specific
    listenersLocation: submissionData?.listenersLocation || [],
    songCategory: submissionData?.songCategory || [],

    // Deezer specific
    priority: submissionData?.priority || "",
    deezerEditorialTeam: submissionData?.deezerEditorialTeam || "",
    deezerPlaylist: submissionData?.deezerPlaylist || [],

    // TIDAL specific
    artistCountry: submissionData?.artistCountry || "",
    LabelDistributor: submissionData?.LabelDistributor || "",
    tidalArtistLevel: submissionData?.tidalArtistLevel || "",
    tidalPriorityLevel: submissionData?.tidalPriorityLevel || "",
    tidalDolbyAtmos: submissionData?.tidalDolbyAtmos || "",
    tidalLocalMarket: submissionData?.tidalLocalMarket || [],
    tidalBpm: submissionData?.tidalBpm || 0,
    tidalConfirmation: submissionData?.tidalConfirmation || null,

    // SoundCloud specific
    promotionTerritories: submissionData?.promotionTerritories || [],
    freshMusicFriday: submissionData?.freshMusicFriday || [],
    playlistConsideration: submissionData?.playlistConsideration || "",
    soundCloudReleaseStage: submissionData?.soundCloudReleaseStage || "",
    soundCloudLabel: submissionData?.soundCloudLabel || "",

    // Status
    status: submissionData?.status || "submitted",

    // YouTube specific
    youtubePromotionSurface: submissionData?.youtubePromotionSurface || [],
    youtubeReleaseStage: submissionData?.youtubeReleaseStage || "",
    // youtubeSongDetails: submissionData?.youtubeSongDetails || [],
    youtubePromotionDate: submissionData?.youtubePromotionDate || "",
    youtubeTerritories: submissionData?.youtubeTerritories || [],
    //assets
    images: submissionData?.images || [],
    //siriusxm specific
    songWriters: submissionData?.songWriters || "",
    siriusXmChannel: submissionData?.siriusXmChannel || "",
    canadianContent: submissionData?.canadianContent || false,
    //contributors
    contributors: submissionData?.contributors || [],

     // ADD THIS LINE - DSP Marketing Notes
    dspMarketingNotes: submissionData?.dspMarketingNotes || [],
    // Platform-specific UPCs
    platformSpecificUpcs: submissionData?.platformSpecificUpcs || [],

    // Vevo specific fields
    vevoContentType: submissionData?.vevoContentType || "",
    vevoPremiereDate: submissionData?.vevoPremiereDate || "",
    vevoContentProvider: submissionData?.vevoContentProvider || "",
    vevoSubgenre: submissionData?.vevoSubgenre || [],
    vevoSubgenreOther: submissionData?.vevoSubgenreOther || "",
    vevoAdditionalNotes: submissionData?.vevoAdditionalNotes || "",
    vevoPreviewLink: submissionData?.vevoPreviewLink || "",
    vevoRep: submissionData?.vevoRep || "",
    vevoRepOther: submissionData?.vevoRepOther || "",
    vevoIsCtvOnly: submissionData?.vevoIsCtvOnly || "",
    vevoIsRefeature: submissionData?.vevoIsRefeature || "",

    // Provisional fields
    provisionalFields: [],
    provisionalNote: "",
    provisionalFieldHistory: [],

    // Set by Apply Suggestions in the AIMS panel
    aimsRequestId: undefined,
  };

  function transformSubmissionData(formData: any) {
    const dsps: any = {};

    // Get DSP marketing notes for easy lookup
    const dspMarketingNotes = formData.dspMarketingNotes || [];
    const notesLookup = dspMarketingNotes.reduce((acc: any, note: any) => {
      acc[note.dspName] = note.note;
      return acc;
    }, {});

    // Get platform-specific UPCs for lookup (grouped by DSP name)
    const platformSpecificUpcs = formData.platformSpecificUpcs || [];
    const upcLookup = platformSpecificUpcs.reduce((acc: any, entry: any) => {
      if (!acc[entry.dspName]) acc[entry.dspName] = [];
      acc[entry.dspName].push({ upc: entry.upc, notes: entry.notes || null });
      return acc;
    }, {});

    if (selectedDSPs.spotify) {
      dsps.spotify = {
        spotifyUri: formData.spotifyUri,
        artistHometown: formData.artistHometown,
        artistCurrentTown: formData.artistCurrentTown,
        pinToPlaylist: formData.pinToThisIsPlaylist,
        subgenre: Array.isArray(formData.spotifySubgenre)
          ? formData.spotifySubgenre.join(", ")
          : formData.spotifySubgenre,
        musicCulture: Array.isArray(formData.musicCulture)
          ? formData.musicCulture.join(", ")
          : formData.musicCulture,
        instruments: Array.isArray(formData.instruments)
          ? formData.instruments.join(", ")
          : formData.instruments,
        songStyle: Array.isArray(formData.songStyle)
          ? formData.songStyle.join(", ")
          : formData.songStyle,
        playlist: Array.isArray(formData.spotifyPlaylist)
          ? formData.spotifyPlaylist.join(", ")
          : formData.spotifyPlaylist,
        // Add marketing note if exists
        marketingNote: notesLookup["Spotify"] || null,
        platformSpecificUpcs: upcLookup["Spotify"] || null,
        // Notable fields
        pitchToNotable: formData.pitchToNotable || false,
        notableSubmissionCategory: formData.notableSubmissionCategory || null,
        notableGlobalMusicProgram: Array.isArray(formData.notableGlobalMusicProgram)
          ? formData.notableGlobalMusicProgram.join(", ")
          : formData.notableGlobalMusicProgram || null,
        notableSubgenre: formData.notableSubgenre || null,
        notableSongwriter: formData.notableSongwriter || null,
      };
    }

    if (selectedDSPs.amazon) {
      dsps.amazon = {
        listenersLocation: Array.isArray(formData.listenersLocation)
          ? formData.listenersLocation.join(", ")
          : formData.listenersLocation,
        similarArtists: formData.similarArtists,
        songCategory: Array.isArray(formData.songCategory)
          ? formData.songCategory.join(", ")
          : formData.songCategory,
        subgenre: Array.isArray(formData.amazonSubgenre)
          ? formData.amazonSubgenre.join(", ")
          : formData.amazonSubgenre,
        asin: formData.asin,
        // Add marketing note if exists
        marketingNote: notesLookup["Amazon Music"] || null,
        platformSpecificUpcs: upcLookup["Amazon Music"] || null,
      };
    }

    if (selectedDSPs.deezer) {
      dsps.deezer = {
        priority: formData.priority,
        editorialTeam: formData.deezerEditorialTeam,
        subgenre: Array.isArray(formData.deezerSubgenre)
          ? formData.deezerSubgenre.join(", ")
          : formData.deezerSubgenre,
        playlist: Array.isArray(formData.deezerPlaylist)
          ? formData.deezerPlaylist.join(", ")
          : formData.deezerPlaylist,
        // Only include whichever field is actually visible for the user's
        // company type - a hidden field may still hold a stale value in
        // form state and must not be submitted.
        distributor: companyTypeCategory !== "distributor" ? formData.distributor : undefined,
        label: companyTypeCategory !== "label" ? formData.soundCloudLabel : undefined,
        // Add marketing note if exists
        marketingNote: notesLookup["Deezer"] || null,
        platformSpecificUpcs: upcLookup["Deezer"] || null,
      };
    }

    if (selectedDSPs.TIDAL) {
      dsps.tidal = {
        subgenre: Array.isArray(formData.TIDALSubgenre)
          ? formData.TIDALSubgenre.join(", ")
          : formData.TIDALSubgenre,
        artistPageLink: formData.TIDALpage,
        country: formData.artistCountry,
        labelDistributor: formData.LabelDistributor,
        socialHandles: formData.socialsLinks,
        artistLevel: formData.tidalArtistLevel,
        priorityLevel: formData.tidalPriorityLevel,
        dolbyAtmos: formData.tidalDolbyAtmos,
        localMarket: Array.isArray(formData.tidalLocalMarket)
          ? formData.tidalLocalMarket.join(", ")
          : formData.tidalLocalMarket,
        bpm: formData.tidalBpm,
        confirmation: formData.tidalConfirmation,
        // Add marketing note if exists
        marketingNote: notesLookup["TIDAL"] || null,
        platformSpecificUpcs: upcLookup["TIDAL"] || null,
      };
    }

    if (selectedDSPs.audiomack) {
      dsps.audiomack = {
        pitchToTrending: formData.pitchToAudiomackTrending,
        subgenre: Array.isArray(formData.audiomackSubgenre)
          ? formData.audiomackSubgenre.join(", ")
          : formData.audiomackSubgenre,
        // Add marketing note if exists
        marketingNote: notesLookup["Audiomack"] || null,
        platformSpecificUpcs: upcLookup["Audiomack"] || null,
      };
    }

    if (selectedDSPs.soundcloud) {
      dsps.soundcloud = {
        // fullName: formData.fullName,
        soundCloudProfileUrl: formData.soundCloudProfileUrl,

        soundCloudContact: formData.soundCloudContact,
        subgenre: Array.isArray(formData.soundCloudSubgenre)
          ? formData.soundCloudSubgenre.join(", ")
          : formData.soundCloudSubgenre,
        isPremierProgram: formData.isPremierProgram,
        // Only include whichever field is actually visible for the user's
        // company type - a hidden field may still hold a stale value in
        // form state and must not be submitted.
        distributor: companyTypeCategory !== "distributor" ? formData.distributor : undefined,
        label: companyTypeCategory !== "label" ? formData.soundCloudLabel : undefined,
        promotionTerritories: Array.isArray(formData.promotionTerritories)
          ? formData.promotionTerritories.join(", ")
          : formData.promotionTerritories,
        freshMusicFriday: Array.isArray(formData.freshMusicFriday)
          ? formData.freshMusicFriday.join(", ")
          : formData.freshMusicFriday,
        playlistConsideration: formData.playlistConsideration,
        releaseStage: formData.soundCloudReleaseStage,
        // Add marketing note if exists
        marketingNote: notesLookup["SoundCloud"] || null,
        platformSpecificUpcs: upcLookup["SoundCloud"] || null,
      };
    }

    if (selectedDSPs.apple) {
      dsps.apple = {
        appleId: formData.appleId,
        appleTrackId: formData.appleTrackId,
        appleAlbumId: formData.appleAlbumId,
        subgenre: Array.isArray(formData.appleSubgenre)
          ? formData.appleSubgenre.join(", ")
          : formData.appleSubgenre,
        releaseStage: formData.appleReleaseStage,
        pitchFrom: formData.applePitchFrom,
        pitchTo: formData.applePitchTo,
        hasTimedRelease: formData.appleHasTimedRelease,
        timedReleaseDate: formData.appleTimedReleaseDate || null,
        hasMotionArtwork: formData.appleHasMotionArtwork,
        hasSpatialAudio: formData.appleHasSpatialAudio,
        label: formData.appleLabel,
        promotionDate: formData.applePromotionDate || null,
        country: formData.appleCountry,
        // Add marketing note if exists
        marketingNote: notesLookup["Apple Music"] || null,
        platformSpecificUpcs: upcLookup["Apple Music"] || null,
      };
    }

    if (selectedDSPs.youtube) {
      dsps.youtube = {
        promotionSurface: Array.isArray(formData.youtubePromotionSurface)
          ? formData.youtubePromotionSurface.join(", ")
          : formData.youtubePromotionSurface,
        songDetails: formData.youtubeReleaseStage,
        promotionDate: formData.youtubePromotionDate || null,
        territories: Array.isArray(formData.youtubeTerritories)
          ? formData.youtubeTerritories.join(", ")
          : formData.youtubeTerritories,
        // Add marketing note if exists
        marketingNote: notesLookup["YouTube"] || null,
        platformSpecificUpcs: upcLookup["YouTube"] || null,
      };
    }

    if (selectedDSPs.siriusxm) {
      dsps.siriusxm = {
        songWriters: formData.songWriters,
        siriusXmChannel: formData.siriusXmChannel,
        canadianContent: formData.canadianContent,
        // Add marketing note if exists
        marketingNote: notesLookup["SiriusXM"] || null,
        platformSpecificUpcs: upcLookup["SiriusXM"] || null,
      };
    }

    if (selectedDSPs.vevo) {
      dsps.vevo = {
        contentType: formData.vevoContentType,
        premiereDate: formData.vevoPremiereDate,
        contentProvider: formData.vevoContentProvider,
        subgenre: Array.isArray(formData.vevoSubgenre)
          ? formData.vevoSubgenre.join(", ")
          : formData.vevoSubgenre,
        subgenreOther: formData.vevoSubgenreOther,
        additionalNotes: formData.vevoAdditionalNotes,
        previewLink: formData.vevoPreviewLink,
        rep: formData.vevoRep,
        repOther: formData.vevoRepOther,
        isCtvOnly: formData.vevoIsCtvOnly,
        isRefeature: formData.vevoIsRefeature,
        // Add marketing note if exists
        marketingNote: notesLookup["Vevo"] || null,
        platformSpecificUpcs: upcLookup["Vevo"] || null,
      };
    }

    return {
      artistName: formData.artistName,
      artistId: artistId,
      releaseTitle: formData.releaseTitle,
      releaseDate: formData.releaseDate,
      configuration: Array.isArray(formData.configuration)
        ? formData.configuration.join(", ")
        : formData.configuration,
      releaseUPC: formData.releaseUpc === "" ? null : formData.releaseUpc,
      focusTrackName: formData.focusTrackName,
      trackISRC: formData.trackIsrc === "" ? null : formData.trackIsrc,
      featuredArtists: formData.featuredArtists,
      featuredArtistURI: formData.featuredArtistUri,
      altTitles: formData.altTitles,
      shortDescription: formData.shortDescription,
      expandedDescription: formData.expandedDescription,
      longDescription: formData.longDescription,
      lyrics: formData.lyrics,
      songDescription: Array.isArray(formData.songDescription)
        ? formData.songDescription.join(", ")
        : formData.songDescription,
      mainGenre: Array.isArray(formData.mainGenre)
        ? formData.mainGenre.join(", ")
        : formData.mainGenre,
      songMood: Array.isArray(formData.songMood)
        ? formData.songMood.join(", ")
        : formData.songMood,
      language: Array.isArray(formData.language)
        ? formData.language.join(", ")
        : formData.language,
      linkToListen: formData.linkToListen,
      promoPhotos: formData.promoPhotos || (Array.isArray(formData.images) && formData.images.length > 0 ? formData.images[0].url : ""),
      promoPhotosCredit: formData.promoPhotosCredit,
      status: formData.status,
      dsps: dsps,
      isEmail: selectedDSPs.email,
      images: formData.images || [],
      contributors: (formData.contributors || []).map(({ id, name, roles }: any) => {
        const rolesArray = typeof roles === 'string'
          ? roles.split(',').map((r: string) => r.trim()).filter((r: string) => r.length > 0)
          : Array.isArray(roles) ? roles : [];

        const contributor: any = { name, role: rolesArray };
        if (id && !id.toString().startsWith('contributor-') && !id.toString().startsWith('temp-')) {
          contributor.id = id;
        }
        return contributor;
      }),
      // Add DSP marketing notes as a separate field for easier backend processing
      // dspMarketingNotes: formData.dspMarketingNotes || [],
      platformSpecificUpcs: formData.platformSpecificUpcs || [],
      provisionalFields: formData.provisionalFields ?? [],
      provisionalNote: formData.provisionalNote ?? null,
      provisionalFieldHistory: formData.provisionalFieldHistory ?? [],
      aimsRequestId: formData.aimsRequestId ?? null,
    };
  }

  // Check OAuth status for email
  const checkEmailOAuthStatus = async (): Promise<boolean> => {
    try {
      const response = await oauthApi.getEmailStatus();
      return response?.data?.gmail?.connected === true || response?.data?.outlook?.connected === true;
    } catch (error) {
      console.error('Failed to check OAuth status:', error);
      return false;
    }
  };

  // Handle email modal send - just store data and close modal (no API hit)
  const handleEmailSend = async (data: {
    contactIds: number[];
    templateId: string;
    subject: string;
    templateContent?: string;
    selectedContacts?: EmailContact[];
    personalizedMessages?: PersonalizedMessage[];
  }) => {
    // The modal writes the messages itself when the template supports them; otherwise keep
    // whatever was staged. Either way, drop notes for contacts that are no longer selected.
    const existingMessages = data.personalizedMessages ?? emailModalData.personalizedMessages ?? [];
    const preservedMessages = existingMessages.filter((m) =>
      data.contactIds.includes(m.contactId)
    );

    // Store the email data for later submission
    setEmailModalData({
      ...data,
      personalizedMessages: preservedMessages,
    });
    setHasEmailData(true);
    setIsEmailModalOpen(false);
    setPendingFormValues(null);

    // Show confirmation that email data is saved
    Swal.fire({
      title: 'Email Configured',
      text: data.contactIds.length > 0
        ? `Email settings saved. ${data.contactIds.length} contact${data.contactIds.length > 1 ? 's' : ''} will receive the pitch email when you submit.`
        : 'Email settings saved. Email will only be sent to you when you submit.',
      icon: 'success',
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
    });
  };

  // Handle opening email modal for editing (without triggering submit)
  const handleEditEmail = (values: PitchFormValues) => {
    setPendingFormValues(values);
    setIsEmailModalOpen(true);
  };

  // Handle Email checkbox selection - open modal only if not already configured
  const handleEmailSelect = async () => {
    // If email is already configured, don't open modal again
    if (hasEmailData && emailModalData.templateId) {
      return;
    }

    // First check OAuth status
    const isOAuthConnected = await checkEmailOAuthStatus();

    if (!isOAuthConnected) {
      // Show error popup blocking selection
      Swal.fire({
        title: "Email Not Connected",
        text: "You need to connect your email account before sending email pitches. Please connect your Gmail or Outlook account in settings.",
        icon: "error",
        showCancelButton: true,
        confirmButtonText: "Connect Email",
        cancelButtonText: "Cancel",
      }).then((result) => {
        if (result.isConfirmed) {
          navigate("/email");
        } else {
          // Uncheck email if user cancels
          setSelectedDSPs(prev => ({ ...prev, email: false }));
        }
      });
      return;
    }

    // OAuth connected - open the email modal
    setPendingFormValues(formikRef.current?.values || null);
    setIsEmailModalOpen(true);
  };

  // Submit pitch with email data
  const submitPitchWithEmail = async (
    values: PitchFormValues,
   emailData: {
     subject:string
          templateId: string, 
          contacts: object,
    }
  ) => {
    setIsSubmitting(true);

    const submissionPayload = {
      ...values,
      status: "submitted",
      selectedPlatforms: Object.entries(selectedDSPs)
        .filter(([_, isSelected]) => isSelected)
        .map(([platform]) => platform),
    };

    try {
      // Call Soundcharts api with trackIsrc
      let soundchartsUUID: string | null = null;

      if (values.trackIsrc) {
        try {
          const soundchartsResponse = await soundchartsEndpoints.getSongByISRC(values.trackIsrc);
          const artistUuid = soundchartsResponse?.data?.object?.artists?.[0]?.uuid;
          soundchartsUUID = artistUuid || null;
        } catch (scError) {
          console.error("Soundcharts API error:", scError);
        }
      }

      // Submit the pitch with email data included
      const Payload = {
        ...transformSubmissionData(submissionPayload),
        soundchartsUUID,
        // Include email data in the pitch creation payload
        emailData
      };

      const response = await pitchesApi.create(Payload);
      if (!response?.data.success) {
        throw new Error(response.statusText || "Submission failed");
      }

      const hasProvisionalEmail = (values.provisionalFields?.length ?? 0) > 0;
      if (hasProvisionalEmail) {
        await Swal.fire({
          title: "Pitch Submitted",
          html: `Your pitch has been submitted. You've flagged <strong>${values.provisionalFields!.length}</strong> field(s) as provisional — remember to resubmit once the final values are confirmed.`,
          icon: "success",
          confirmButtonText: "Go to Dashboard",
        });
      } else {
        await Swal.fire({
          title: "Success!",
          text: emailData.templateId
            ? `Pitch submitted and email${Object.keys(emailData.contacts).length > 1 ? 's' : ''} sent successfully!`
            : "Pitch submitted successfully!",
          icon: "success",
          confirmButtonText: "OK",
        });
      }

      navigate("/dashboard");
    } catch (error: any) {
      console.error("Submission error:", error);

      if (await handleUpcConflict(error, navigate)) {
        return;
      }

      const errorMessages =
        error.response?.data?.details &&
          Array.isArray(error.response.data.details)
          ? error.response.data.details
            .map(
              (detail: { field: string; message: string }) =>
                `<b>${detail.field}</b>: ${detail.message}`
            )
            .join("<br>")
          : error.response?.data?.errors &&
            Array.isArray(error.response.data.errors)
            ? error.response.data.errors
              .map((message: string) => `${message}`)
              .join("<br>")
            : error.response?.data?.message || "An unexpected error occurred";

      Swal.fire({
        title: "Error!",
        html: errorMessages,
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setIsSubmitting(false);
      setPendingFormValues(null);
    }
  };

  const handleSubmit = async (values: PitchFormValues) => {
    setIsSubmitting(true);

    const submissionPayload = {
      ...values,
      status: "submitted",
      selectedPlatforms: Object.entries(selectedDSPs)
        .filter(([_, isSelected]) => isSelected)
        .map(([platform]) => platform),
    };

    if (Object.keys(transformSubmissionData(submissionPayload).dsps).length === 0) {
      Swal.fire({
        title: "Error!",
        text: "Please select at least one platform to submit your pitch.",
        icon: "error",
        confirmButtonText: "OK",
      });
      setIsSubmitting(false);
      return;
    }

    // Validate that selected platforms have connected DSP accounts
    const validation = validateSelectedDSPs(selectedDSPs, dspList);
    if (!validation.isValid) {
      const missingPlatformsText = validation.missingPlatforms
        .map(p => p.toUpperCase())
        .join(', ');

      Swal.fire({
        title: "Error!",
        text: `You don't have connected accounts for: ${missingPlatformsText}. Please connect these DSPs before submitting a pitch.`,
        showCancelButton: true,
        cancelButtonText: "Cancel",
        confirmButtonText: "Connect DSPs",
      }).then(async (result) => {
        if (result.isConfirmed) {
          // Save draft before navigating to DSP settings
          try {
            const draftPayload = {
              ...submissionPayload,
              status: "draft"
            };
            await pitchesApi.saveDraft(
              transformSubmissionData(draftPayload)
            );
          } catch (draftError) {
            console.error("Failed to save draft before navigation:", draftError);
          }
          navigate("/settings?tab=dsps");
        }
      });
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await dspApi.getDSPs();
      if (response?.data?.data?.length === 0) {
        Swal.fire({
          title: "Error!",
          text: "No DSPs connected. Please connect at least one DSP before submitting a pitch.",
          showCancelButton: true,
          confirmButtonText: "Connect DSPs",
          cancelButtonText: "Cancel",
        }).then(async (result) => {
          if (result.isConfirmed) {
            // Save draft before navigating to DSP settings
            try {
              const draftPayload = {
                ...submissionPayload,
                status: "draft"
              };
              await pitchesApi.saveDraft(transformSubmissionData(draftPayload));
            } catch (draftError) {
              console.error("Failed to save draft before navigation:", draftError);
            }
            navigate("/settings?tab=dsps");
          }
        });
        setIsSubmitting(false);
        return;
      }
    } catch (error) {
      console.error("Error fetching DSPs:", error);
      Swal.fire({
        title: "Error!",
        text: "Failed to verify connected DSPs. Please try again later.",
        icon: "error",
        confirmButtonText: "OK",
      });
      setIsSubmitting(false);
      return;
    }

    // Check if email is selected - validate email data is configured
    if (selectedDSPs.email) {
      const contactsMap: Record<string, string> = {};
        (emailModalData.selectedContacts || []).forEach((contact) => {
          contactsMap[String(contact.id)] =
            emailModalData.personalizedMessages?.find(
              (m) => m.contactId === contact.id
            )?.message || "";
        });
      // Check if email data already exists (user already configured email via checkbox)
      if (hasEmailData && emailModalData.templateId) {
        // Validate subject is provided before submission
        if (!emailModalData.subject?.trim()) {
          Swal.fire({
            title: "Subject Required",
            text: "Please add an email subject before submitting. Click 'Edit Email' to add a subject.",
            icon: "warning",
            confirmButtonText: "OK",
          });
          setIsSubmitting(false);
          return;
        }
        const transformEmailData = {
          subject: emailModalData.subject,
          templateId: emailModalData.templateId,
          contacts: contactsMap,
        };
        // Directly submit with existing email data
        await submitPitchWithEmail(values, transformEmailData);
        return;
      }

      // Email selected but not configured - prompt user to configure
      Swal.fire({
        title: "Email Not Configured",
        text: "Please configure your email settings by clicking the 'Edit Email' button or re-selecting the Email checkbox.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Configure Email",
        cancelButtonText: "Cancel",
      }).then((result) => {
        if (result.isConfirmed) {
          // Open email modal
          handleEmailSelect();
        }
      });
      setIsSubmitting(false);
      return;
    }

    try {
      const Payload = transformSubmissionData(submissionPayload)

      // Submit the pitch
      const response = await pitchesApi.create(Payload);
      if (!response?.data.success) {
        throw new Error(response.statusText || "Submission failed");
      }

      const hasProvisional = (submissionPayload.provisionalFields?.length ?? 0) > 0;
      if (hasProvisional) {
        await Swal.fire({
          title: "Pitch Submitted",
          html: `Your pitch has been submitted. You've flagged <strong>${submissionPayload.provisionalFields!.length}</strong> field(s) as provisional — remember to resubmit once the final values are confirmed.`,
          icon: "success",
          confirmButtonText: "Go to Dashboard",
        });
      }
      navigate("/dashboard");
    } catch (error: any) {
      console.error("Submission error:", error);

      if (await handleUpcConflict(error, navigate)) {
        return;
      }

      const errorMessages =
        error.response?.data?.details &&
          Array.isArray(error.response.data.details)
          ? error.response.data.details
            .map(
              (detail: { field: string; message: string }) =>
                `<b>${detail.field}</b>: ${detail.message}`
            )
            .join("<br>")
          : error.response?.data?.errors &&
            Array.isArray(error.response.data.errors)
            ? error.response.data.errors
              .map((message: string) => `${message}`)
              .join("<br>")

            : error.response?.data?.message || "An unexpected error occurred";


      Swal.fire({
        title: "Error!",
        html: errorMessages,
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveDraft = async (values: PitchFormValues) => {
    setIsDrafting(true);

    const submissionPayload = {
      ...values,
      status: "draft",
      selectedPlatforms: Object.entries(selectedDSPs)
        .filter(([_, isSelected]) => isSelected)
        .map(([platform]) => platform),
    };

    try {
      const draftPayload: any = transformSubmissionData(submissionPayload);

      // Include email data if configured
      if (selectedDSPs.email && hasEmailData && emailModalData.templateId) {
        const contactsMap: Record<string, string> = {};
        (emailModalData.selectedContacts || []).forEach((contact) => {
          contactsMap[String(contact.id)] =
            emailModalData.personalizedMessages?.find(
              (m) => m.contactId === contact.id
            )?.message || "";
        });
        draftPayload.emailData = {
          subject: emailModalData.subject,
          templateId: emailModalData.templateId,
          contacts: contactsMap,
        };
      }

      const response = await pitchesApi.saveDraft(draftPayload);

      if (!response?.data.success) {
        throw new Error(response.statusText || "Draft save failed");
      }

      Swal.fire({
        title: "Success!",
        text: "Draft saved successfully",
        icon: "success",
        confirmButtonText: "OK",
      });
      navigate("/dashboard");
    } catch (error: any) {
      console.error("Draft save error:", error);

      if (await handleUpcConflict(error, navigate)) {
        return;
      }

      Swal.fire({
        title: "Error!",
        text: error.response?.data?.message || "Failed to save draft",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setIsDrafting(false);
    }
  };

  // Function to scroll to the first error
  const scrollToFirstError = (errors: any) => {
    const errorKeys = Object.keys(errors);
    if (errorKeys.length === 0) return;

    const firstErrorKey = errorKeys[0];
    const errorField = document.querySelector(`[name="${firstErrorKey}"]`);

    if (errorField) {
      errorField.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });

      errorField.classList.add('ring-2', 'ring-danger-500', 'bg-danger-50');
      setTimeout(() => {
        errorField.classList.remove('ring-2', 'ring-danger-500', 'bg-danger-50');
      }, 3000);
    }
  };

  // Function to show error toast
  const showErrorToast = (errors: any) => {
    const errorCount = Object.keys(errors).length;
    const errorFields = Object.keys(errors).slice(0, 3).join(', ');
    const remainingCount = errorCount > 3 ? errorCount - 3 : 0;

    let errorMessage = `Please fix ${errorCount} validation error${errorCount > 1 ? 's' : ''}.`;
    if (errorCount <= 3) {
      errorMessage += ` Fields: ${errorFields}`;
    } else {
      errorMessage += ` Fields: ${errorFields}${remainingCount > 0 ? ` and ${remainingCount} more` : ''}`;
    }

    Swal.fire({
      title: 'Validation Errors',
      text: errorMessage,
      icon: 'error',
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 4000,
      timerProgressBar: true,
      didOpen: (toast) => {
        toast.addEventListener('mouseenter', Swal.stopTimer)
        toast.addEventListener('mouseleave', Swal.resumeTimer)
      }
    });
  };

  const handleDownloadAppleCsv = () => {
    const values = formikRef.current?.values;
    if (!values) return;
    downloadAppleCsv({
      id: "",
      artistName: values.artistName,
      releaseTitle: values.releaseTitle,
      releaseDate: values.releaseDate,
      configuration: Array.isArray(values.configuration) ? values.configuration.join(", ") : values.configuration,
      releaseUPC: values.releaseUpc,
      focusTrackName: values.focusTrackName || "",
      trackISRC: values.trackIsrc || "",
      songDescription: Array.isArray(values.songDescription) ? values.songDescription.join(", ") : values.songDescription || "",
      mainGenre: Array.isArray(values.mainGenre) ? values.mainGenre.join(", ") : values.mainGenre,
      songMood: Array.isArray(values.songMood) ? values.songMood.join(", ") : values.songMood,
      language: Array.isArray(values.language) ? values.language.join(", ") : values.language,
      featuredArtists: values.featuredArtists || "",
      shortDescription: values.shortDescription,
      longDescription: values.longDescription,
      lyrics: values.lyrics,
      linkToListen: values.linkToListen || "",
      promoPhotos: values.promoPhotos || "",
      promoPhotosCredit: values.promoPhotosCredit || "",
      status: "submitted",
      AppleInfo: {
        appleId: values.appleId,
        appleTrackId: values.appleTrackId,
        subgenre: Array.isArray(values.appleSubgenre) ? values.appleSubgenre.join(", ") : values.appleSubgenre,
        releaseStage: values.appleReleaseStage,
        pitchFrom: values.applePitchFrom,
        pitchTo: values.applePitchTo,
        hasTimedRelease: values.appleHasTimedRelease,
        timedReleaseDate: values.appleTimedReleaseDate,
        hasMotionArtwork: values.appleHasMotionArtwork,
        hasSpatialAudio: values.appleHasSpatialAudio,
        label: values.appleLabel,
        promotionDate: values.applePromotionDate,
        country: values.appleCountry,
      },
    });
  };

  // Create dynamic validation schema based on selected DSPs
  const validationSchema = React.useMemo(() => {
    return createPitchValidationSchema(selectedDSPs, companyTypeCategory);
  }, [selectedDSPs, companyTypeCategory]);

  return (
    <div className="py-2 sm:p-6">
      <Breadcrumbs items={[HOME_CRUMB, { label: "Create Pitch" }]} />

      {/* Remove the Error Summary Banner section */}

      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, setFieldValue }) => (
          <Form>
            <OnInvalidSubmit
              onInvalid={(submitErrors) => {
                showErrorToast(submitErrors);
                scrollToFirstError(submitErrors);
              }}
            />
            <div className="mb-8">
              <ReleaseInfo />
            </div>

            <div className="mb-8">
              <PlatformSelection
                selectedDSPs={selectedDSPs}
                setSelectedDSPs={setSelectedDSPs}
                onDspDeselect={handleDspDeselect}
                onEmailSelect={handleEmailSelect}
                dspList={dspList}
              />
            </div>

            <div className="mb-8">
              <GeneralReleaseInfo
                setIsAddArtistModalOpen={setIsAddArtistModalOpen}
                setArtistId={setArtistId}
                selectedDSPs={selectedDSPs}
                emailData={hasEmailData ? emailModalData : undefined}
              />
            </div>

            {hasDspSpecificFields(selectedDSPs) && (
              <div className="mb-8">
                <DspSpecificInfo selectedDSPs={selectedDSPs} onDownloadAppleCsv={handleDownloadAppleCsv} companyType={companyType} />
              </div>
            )}

            <div className="mb-4">
              <ProvisionalFieldsControl
                values={values}
                setFieldValue={setFieldValue}
                selectedDSPs={selectedDSPs}
              />
            </div>

            <div className="flex justify-end gap-4 mt-8 ml-auto w-fit" data-tour="pitch-form-buttons">
              <Button
                type="button"
                variant="neutral"
                onClick={() => navigate("/dashboard")}
                disabled={isSubmitting || isDrafting}
              >
                Cancel
              </Button>

              <Button
                type="button"
                variant="secondary"
                onClick={() => handleSaveDraft(values)}
                loading={isDrafting}
                disabled={isSubmitting}
              >
                {isDrafting ? "Saving Draft..." : "Save Draft"}
              </Button>

              {/* Edit Email button - only show when email is selected and email data has been configured */}
              {selectedDSPs.email && hasEmailData && (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => handleEditEmail(values)}
                  disabled={isSubmitting || isDrafting}
                >
                  <EnvelopeIcon className="w-4 h-4" />
                  Edit Email
                </Button>
              )}

              <Button
                type="submit"
                loading={isSubmitting}
                disabled={isDrafting}
              >
                {isSubmitting ? "Submitting..." : "Submit Pitch"}
              </Button>
            </div>
          </Form>
        )}
      </Formik>

      {isAddArtistModalOpen && (
        <AddNewArtistModal
          isOpen={isAddArtistModalOpen}
          onClose={() => setIsAddArtistModalOpen(false)}
        />
      )}

      {/* Email Pitch Modal */}
      <EmailPitchModal
        isOpen={isEmailModalOpen}
        onClose={() => {
          setIsEmailModalOpen(false);
          setPendingFormValues(null);
          if (!hasEmailData) {
            setSelectedDSPs((prev) => ({ ...prev, email: false }));
          }
        }}
        onSend={handleEmailSend}
        pitchData={pendingFormValues}
        selectedDSPs={Object.entries(selectedDSPs)
          .filter(([_, isSelected]) => isSelected)
          .map(([platform]) => platform)}
        // The pitch has no id yet, so the modal's sessionStorage draft recovery is
        // inactive here. Feed the staged config back in so reopening via "Edit Email"
        // repopulates instead of resetting to a blank form.
        initialEmailData={initialEmailData}
      />

    </div>
  );
};

export default FormPage;